// #873 — Topic-activity dashboard section on /admin/users/<id>/conversations.
//
// Four hand-rolled SVG panels above the existing companion-calls +
// conversations tables:
//
//   1. Topic shares pie       — top-N played-card + wildcard topics
//   2. Daily occurrences line — per-day count of each top-N topic
//   3. Outcome balance pie    — played / declined / wildcard
//   4. Card-type balance pie  — conversation / game / multimedia /
//                               event / news / reminiscence / wildcard
//
// Toggle Week | Month + prev/next stepper. Read-only — pulls from
// `/api/admin/users/<id>/topic-activity?window=<w>&offset=<n>`.
//
// No charting library, per the existing admin pattern
// (AdminAnalytics, AdminCardsVariety, etc.).
import React, { useEffect, useState, useMemo } from 'react';
import { adminFetch } from './userSubPageStyles';

// Stable palette — slot 0..5 for top-N topics, slot 6 for "Other".
// Mapping by ranking position (not by topic_id hash) so that the
// same topic in the same window has the same color in both Panel 1
// (pie) and Panel 2 (lines). Colors chosen for the AmigoMio admin
// surface — coral primary + complementary tones, all visible on the
// white card background.
const TOPIC_PALETTE = [
  '#E8845A',  // coral (primary)
  '#5B8DEF',  // sky
  '#27AE60',  // green
  '#B8860B',  // amber
  '#8E44AD',  // purple
  '#16A085',  // teal
  '#999999',  // Other (always last)
];

// Outcome-pie palette — semantic, not positional. Matches the
// convention pinned in TS-32-01: green=played, amber=skipped,
// red=declined. v2.2 split skipped out of declined — they were
// lumped together in v2.1, mislabelling ~90 skipped cards as
// declined for every 11 truly-declined ones in real usage.
// #886 — six-slice outcome palette covering the flat 5-state taxonomy
// plus wildcard. Mirrors CardsHandView's outcome colour scheme so a
// row's badge and the dashboard's pie slice agree on hue at a glance.
const OUTCOME_COLORS = {
  played:      '#27AE60',  // green: user engaged
  declined:    '#C0392B',  // red:   user actively refused
  ignored:     '#D9A227',  // amber: Berta raised it, user pivoted
  passed_over: '#8E5BAF',  // purple: user picked a sibling card
  unraised:    '#8B95A0',  // grey:  Berta never raised it
  wildcard:    '#5B8DEF',  // blue:  off-script
};

// Card-type palette — by catalogue group.
// #885 — `news` (talk_briefing) gets its own slice; deeper amber
// than `event` so the two earth tones don't blend into one wedge.
// #963 — `reminiscence` (reminisce_* content + probe cards) gets its
// own slice; rose/mauve, distinct from the coral `conversation` so
// the under-served reminiscence-mode share reads at a glance.
const TYPE_COLORS = {
  conversation: '#E8845A',
  game:         '#8E44AD',
  multimedia:   '#16A085',
  event:        '#B8860B',
  news:         '#C26A1A',
  reminiscence: '#C2548A',
  wildcard:     '#5B8DEF',
};

// ── Inline SVG components ────────────────────────────────────────────

/**
 * Donut/pie chart from a list of {label, value, color}. Renders nothing
 * (component returns null) when total == 0; the parent decides what to
 * show in the empty state.
 */
function PieChart({ slices, size = 160, hole = 0 }) {
  const total = slices.reduce((s, sl) => s + (sl.value || 0), 0);
  if (total <= 0) return null;
  const cx = size / 2, cy = size / 2;
  const r = size / 2 - 2;
  // Walk the slices clockwise from 12 o'clock.
  let angle = -Math.PI / 2;
  const paths = [];
  for (let i = 0; i < slices.length; i++) {
    const sl = slices[i];
    if (!sl.value) continue;
    const frac = sl.value / total;
    const next = angle + frac * 2 * Math.PI;
    const x1 = cx + r * Math.cos(angle);
    const y1 = cy + r * Math.sin(angle);
    const x2 = cx + r * Math.cos(next);
    const y2 = cy + r * Math.sin(next);
    const large = frac > 0.5 ? 1 : 0;
    // Degenerate single-slice case (frac == 1) → SVG arc can't render
    // a 360° arc with a single path; draw it as a full circle.
    if (frac >= 0.9999) {
      paths.push(
        <circle key={i} cx={cx} cy={cy} r={r} fill={sl.color} stroke="#fff" strokeWidth="1.5" />
      );
    } else {
      const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
      paths.push(
        <path key={i} d={d} fill={sl.color} stroke="#fff" strokeWidth="1.5">
          <title>{`${sl.label}: ${sl.value} (${Math.round(frac * 100)}%)`}</title>
        </path>
      );
    }
    angle = next;
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="Pie chart">
      {paths}
      {hole > 0 && <circle cx={cx} cy={cy} r={hole} fill="#fff" />}
    </svg>
  );
}

/**
 * Multi-line chart: one line per series, X = days, Y = count per day.
 * Series is [{label, color, per_day: number[]}, ...]. All per_day
 * arrays MUST have the same length (= window.days).
 */
function LineChart({ series, days, monthMode = false, width = 480, height = 220 }) {
  if (!series || series.length === 0 || days <= 0) return null;
  const P_LEFT = 32, P_RIGHT = 12, P_TOP = 14, P_BOT = 30;
  const W = width, H = height;
  const innerW = W - P_LEFT - P_RIGHT;
  const innerH = H - P_TOP - P_BOT;

  // Y-scale: 0 to max observed, at least 2 so small samples don't
  // look flat. Round up to a tidy integer.
  let maxY = 0;
  for (const s of series) for (const v of s.per_day) if (v > maxY) maxY = v;
  maxY = Math.max(2, maxY);

  const xFor = (i) =>
    P_LEFT + (days === 1 ? innerW / 2 : (i * innerW) / (days - 1));
  const yFor = (v) => P_TOP + innerH - (v / maxY) * innerH;

  // Day-axis labels. Week mode: Mon Tue Wed ... Sun.
  // Month mode: 1, 5, 10, 15, 20, 25, 30 — keep it sparse.
  const dayLabel = (i) => {
    if (!monthMode) {
      const wk = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return wk[i] || '';
    }
    const n = i + 1;
    if (n === 1 || n === days || n % 5 === 0) return String(n);
    return '';
  };

  // Y-axis ticks: 0, mid, max.
  const yTicks = [0, Math.ceil(maxY / 2), maxY];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ background: '#FAF8F5', borderRadius: 8 }} aria-label="Daily occurrences chart">
      {/* Y gridlines + labels */}
      {yTicks.map(v => (
        <g key={`y${v}`}>
          <line x1={P_LEFT} y1={yFor(v)} x2={W - P_RIGHT} y2={yFor(v)}
                stroke="#EDE9E4" strokeWidth="1" />
          <text x={P_LEFT - 6} y={yFor(v) + 4} fill="#888" fontSize="10" textAnchor="end">
            {v}
          </text>
        </g>
      ))}
      {/* X axis labels */}
      {Array.from({ length: days }, (_, i) => {
        const lbl = dayLabel(i);
        if (!lbl) return null;
        return (
          <text key={`x${i}`} x={xFor(i)} y={H - 10} fill="#888" fontSize="10" textAnchor="middle">
            {lbl}
          </text>
        );
      })}
      {/* Lines */}
      {series.map((s, si) => {
        const pts = s.per_day.map((v, i) => `${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`).join(' ');
        return (
          <g key={si}>
            <polyline fill="none" stroke={s.color} strokeWidth="2" points={pts} />
            {s.per_day.map((v, i) => (
              <circle key={i} cx={xFor(i)} cy={yFor(v)} r="2.5" fill={s.color}>
                <title>{`${s.label} · ${dayLabel(i) || `day ${i + 1}`}: ${v}`}</title>
              </circle>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Legend row for a chart — colored dot + label + optional count/percent.
 */
function LegendRow({ color, label, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: '0.85rem' }}>
      <span style={{
        display: 'inline-block', width: 10, height: 10, borderRadius: 2,
        background: color, flexShrink: 0,
      }} />
      <span style={{ flex: 1, color: '#2C2C2C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {count != null && (
        <span style={{ color: '#666', fontVariantNumeric: 'tabular-nums' }}>
          {count}{pct != null ? ` · ${pct}%` : ''}
        </span>
      )}
    </div>
  );
}

// ── Main section ─────────────────────────────────────────────────────

// `windowType` rather than `window` so we don't shadow the global
// `window` object in the closure body — purely a readability win.
export default function TopicActivitySection({ userId, onDataChange }) {
  const [windowType, setWindowType] = useState('week');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await adminFetch(
          `/api/admin/users/${userId}/topic-activity?window=${windowType}&offset=${offset}`,
        );
        if (cancelled) return;
        setData(r || null);
        setError(null);
        // The page (AdminUserCards) lifts the same response up to
        // populate the grid below this section, so both views share
        // a single round-trip.
        if (onDataChange) onDataChange(r || null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, windowType, offset, onDataChange]);

  // Reset offset to 0 whenever the user flips the toggle — otherwise
  // "back 3 weeks" silently becomes "back 3 months" on switch.
  function switchWindow(next) {
    if (next === windowType) return;
    setOffset(0);
    setWindowType(next);
  }

  const topicSeries = useMemo(() => {
    if (!data || !data.topics) return [];
    return data.topics.map((t, i) => ({
      label: t.display,
      color: TOPIC_PALETTE[i] || TOPIC_PALETTE[TOPIC_PALETTE.length - 1],
      per_day: t.per_day,
      count: t.count,
      topic_id: t.topic_id,
    }));
  }, [data]);

  const totalTopicCount = topicSeries.reduce((s, t) => s + (t.count || 0), 0);
  const monthMode = data?.window?.window_type === 'month';

  return (
    <div style={{ marginBottom: 32 }}>
      <style>{`
        .ta-section { background: #fff; border-radius: 12px;
                      box-shadow: 0 2px 12px rgba(0,0,0,0.08);
                      padding: 18px 20px; margin-bottom: 16px; }
        .ta-header  { display: flex; align-items: center; gap: 12px;
                      margin-bottom: 12px; flex-wrap: wrap; }
        .ta-title   { font-size: 0.95rem; font-weight: 700; color: #2C2C2C;
                      letter-spacing: -0.01em; }
        .ta-sub     { color: #888; font-size: 0.82rem; }
        .ta-toggle  { display: inline-flex; border: 1px solid #DDD;
                      border-radius: 6px; overflow: hidden;
                      margin-left: auto; }
        .ta-toggle button {
          background: #fff; border: none; padding: 5px 14px;
          font-size: 0.85rem; color: #2C2C2C; cursor: pointer;
          font-family: inherit;
        }
        .ta-toggle button.active { background: #E8845A; color: #fff; font-weight: 600; }
        .ta-stepper { display: inline-flex; align-items: center; gap: 6px;
                      font-size: 0.82rem; color: #666; }
        .ta-stepper button {
          background: #fff; border: 1px solid #DDD; border-radius: 4px;
          width: 24px; height: 24px; cursor: pointer; font-family: inherit;
          color: #2C2C2C;
        }
        .ta-stepper button:disabled { color: #CCC; cursor: not-allowed; }
        .ta-stepper button:hover:not(:disabled) { background: #FFF9F5; }
        .ta-window-label { min-width: 140px; text-align: center;
                           font-weight: 600; color: #2C2C2C; font-size: 0.85rem; }
        .ta-grid    { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        @media (max-width: 900px) { .ta-grid { grid-template-columns: 1fr; } }
        .ta-panel   { background: #FAF8F5; border-radius: 8px; padding: 14px; }
        .ta-panel-title { font-size: 0.82rem; font-weight: 700; color: #2C2C2C;
                          text-transform: uppercase; letter-spacing: 0.05em;
                          margin-bottom: 10px; }
        .ta-pie-and-legend { display: flex; align-items: flex-start; gap: 14px; }
        .ta-legend  { flex: 1; min-width: 0; }
        .ta-empty   { color: #888; font-size: 0.88rem; font-style: italic;
                      text-align: center; padding: 36px 12px; }
      `}</style>

      <div className="section-title" style={{ marginTop: 0 }}>
        Topic activity
        <span className="section-sub">
          What this user actually talked about · from the cards system
        </span>
      </div>

      <div className="ta-section">
        <div className="ta-header">
          <div className="ta-sub">
            {data?.window?.label || (loading ? 'Loading…' : '')}
          </div>
          <div className="ta-stepper" style={{ marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={() => setOffset(o => o + 1)}
              disabled={loading || offset >= 24}
              aria-label="Previous window"
              title="Previous window"
            >
              ‹
            </button>
            <span className="ta-window-label">
              {offset === 0 ? 'Current' : `−${offset} ${windowType}${offset > 1 ? 's' : ''}`}
            </span>
            <button
              type="button"
              onClick={() => setOffset(o => Math.max(0, o - 1))}
              disabled={loading || offset === 0}
              aria-label="Next window"
              title="Next window"
            >
              ›
            </button>
          </div>
          <div className="ta-toggle">
            <button
              type="button"
              className={windowType === 'week' ? 'active' : ''}
              onClick={() => switchWindow('week')}
            >
              Week
            </button>
            <button
              type="button"
              className={windowType === 'month' ? 'active' : ''}
              onClick={() => switchWindow('month')}
            >
              Month
            </button>
          </div>
        </div>

        {loading && <div className="ta-empty">Loading topic activity…</div>}
        {error && <div className="ta-empty" style={{ color: '#C0392B' }}>Error: {error}</div>}

        {!loading && !error && data && data.empty && (
          <div className="ta-empty">
            No topic activity in this window.
          </div>
        )}

        {!loading && !error && data && !data.empty && (
          <>
            {/* Row 1 — topic shares + daily line */}
            <div className="ta-grid">
              <div className="ta-panel">
                <div className="ta-panel-title">Topic shares</div>
                <div className="ta-pie-and-legend">
                  <PieChart
                    slices={topicSeries.map(t => ({
                      label: t.label, value: t.count, color: t.color,
                    }))}
                    size={160}
                  />
                  <div className="ta-legend">
                    {topicSeries.map(t => (
                      <LegendRow
                        key={t.topic_id}
                        color={t.color}
                        label={t.label}
                        count={t.count}
                        total={totalTopicCount}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="ta-panel">
                <div className="ta-panel-title">Occurrences per day</div>
                <LineChart
                  series={topicSeries.filter(t => t.topic_id !== '__other__')}
                  days={data.window.days}
                  monthMode={monthMode}
                />
              </div>
            </div>

            {/* Row 2 — outcome balance + type balance */}
            <div className="ta-grid" style={{ marginTop: 14 }}>
              <div className="ta-panel">
                <div className="ta-panel-title">Outcome balance</div>
                <div className="ta-pie-and-legend">
                  {/* #886 — six-slice pie: five flat outcome states +
                      wildcard. `|| 0` defensive across all keys because
                      pre-#886 conversations (still rendered for windows
                      that include them) may have `null` slots for
                      states that didn't exist yet at write time. */}
                  <PieChart
                    slices={[
                      { label: 'Played',      value: data.outcome_balance.played || 0,      color: OUTCOME_COLORS.played      },
                      { label: 'Declined',    value: data.outcome_balance.declined || 0,    color: OUTCOME_COLORS.declined    },
                      { label: 'Ignored',     value: data.outcome_balance.ignored || 0,     color: OUTCOME_COLORS.ignored     },
                      { label: 'Passed over', value: data.outcome_balance.passed_over || 0, color: OUTCOME_COLORS.passed_over },
                      { label: 'Unraised',    value: data.outcome_balance.unraised || 0,    color: OUTCOME_COLORS.unraised    },
                      { label: 'Wildcard',    value: data.outcome_balance.wildcard || 0,    color: OUTCOME_COLORS.wildcard    },
                    ]}
                    size={140}
                  />
                  <div className="ta-legend">
                    {(() => {
                      const ob = data.outcome_balance || {};
                      const total = (ob.played || 0) + (ob.declined || 0)
                        + (ob.ignored || 0) + (ob.passed_over || 0)
                        + (ob.unraised || 0) + (ob.wildcard || 0);
                      return [
                        ['Played',      ob.played || 0,      OUTCOME_COLORS.played],
                        ['Declined',    ob.declined || 0,    OUTCOME_COLORS.declined],
                        ['Ignored',     ob.ignored || 0,     OUTCOME_COLORS.ignored],
                        ['Passed over', ob.passed_over || 0, OUTCOME_COLORS.passed_over],
                        ['Unraised',    ob.unraised || 0,    OUTCOME_COLORS.unraised],
                        ['Wildcard',    ob.wildcard || 0,    OUTCOME_COLORS.wildcard],
                      ].map(([label, count, color]) => (
                        <LegendRow
                          key={label} color={color} label={label} count={count}
                          total={total}
                        />
                      ));
                    })()}
                  </div>
                </div>
              </div>
              <div className="ta-panel">
                <div className="ta-panel-title">Card-type balance</div>
                <div className="ta-pie-and-legend">
                  {/* #963 — `reminiscence` is the seventh slice. `|| 0`
                      defensive on both news + reminiscence so a frontend
                      deployed ahead of the backend (which adds the key)
                      doesn't render NaN — the backend/frontend deploys
                      aren't atomic. */}
                  <PieChart
                    slices={[
                      { label: 'Conversation', value: data.type_balance.conversation,      color: TYPE_COLORS.conversation },
                      { label: 'Game',         value: data.type_balance.game,              color: TYPE_COLORS.game         },
                      { label: 'Multimedia',   value: data.type_balance.multimedia,        color: TYPE_COLORS.multimedia   },
                      { label: 'Event',        value: data.type_balance.event,             color: TYPE_COLORS.event        },
                      { label: 'News',         value: data.type_balance.news || 0,         color: TYPE_COLORS.news         },
                      { label: 'Reminiscence', value: data.type_balance.reminiscence || 0, color: TYPE_COLORS.reminiscence },
                      { label: 'Wildcard',     value: data.type_balance.wildcard,          color: TYPE_COLORS.wildcard     },
                    ]}
                    size={140}
                  />
                  <div className="ta-legend">
                    {[
                      ['Conversation', data.type_balance.conversation,      TYPE_COLORS.conversation],
                      ['Game',         data.type_balance.game,              TYPE_COLORS.game],
                      ['Multimedia',   data.type_balance.multimedia,        TYPE_COLORS.multimedia],
                      ['Event',        data.type_balance.event,             TYPE_COLORS.event],
                      ['News',         data.type_balance.news || 0,         TYPE_COLORS.news],
                      ['Reminiscence', data.type_balance.reminiscence || 0, TYPE_COLORS.reminiscence],
                      ['Wildcard',     data.type_balance.wildcard,          TYPE_COLORS.wildcard],
                    ].map(([label, count, color]) => (
                      <LegendRow
                        key={label} color={color} label={label} count={count}
                        total={
                          data.type_balance.conversation +
                          data.type_balance.game +
                          data.type_balance.multimedia +
                          data.type_balance.event +
                          (data.type_balance.news || 0) +
                          (data.type_balance.reminiscence || 0) +
                          data.type_balance.wildcard
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
